// Top-level route tree — composes AdminLayout with registry-generated routes.
// SignIn and other pre-auth routes live outside AdminLayout (§14.11).

import { Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { AdminLayout } from './AdminLayout'
import { AuthGate } from './AuthGate'
import { WatchLayout } from './WatchLayout'
import { useWatchMode } from '@/state/useWatchMode'
import { LandingRedirect } from './LandingRedirect'
import { groupRoutes } from './buildGroupRoutes'
import { APP_BASE, APP_LANDING_PATH } from './navRegistry'
import { lazy, useEffect, Suspense } from 'react'
import { useAuth } from '@/state/useAuth'
import { SignInPage } from '@/groups/auth/pages/SignInPage'
import { VerifyEmailPage } from '@/groups/auth/pages/VerifyEmailPage'
import { AcceptInvitePage } from '@/groups/auth/pages/AcceptInvitePage'
import { consumeInviteRedirect, isInviteRedirect } from '@/lib/inviteRedirect'

const PrivacyPage = lazy(() => import('@/legal/PrivacyPage'))
const TermsPage = lazy(() => import('@/legal/TermsPage'))

function SignInRoute() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  function handleSuccess() {
    // After auth, return to a pending invite if one is in-flight (query param
    // on this visit, or stashed in localStorage across the verify round-trip).
    const stored = consumeInviteRedirect()
    const target = params.get('redirect') ?? stored
    navigate(isInviteRedirect(target) ? target : APP_LANDING_PATH, { replace: true })
  }
  return <SignInPage onSuccess={handleSuccess} />
}

/**
 * The whole route tree, while this browser is locked on one panel
 * (`todo-takeout.md` entry 6.25).
 *
 * **A different tree, not a guard sprinkled through the ordinary one.** The
 * legal pages and the sign-in route sit outside `AdminLayout`, so a guard
 * there would have left three routes a typed URL still reaches. Replacing the
 * tree means the catch-all is the only answer to anything that is not the
 * locked panel.
 *
 * `groupRoutes()` is still mounted, because the locked panel has to render
 * through its own route — with its premium gate and its lazy chunk. What keeps
 * the rest unreachable is `WatchLayout`'s redirect, which runs before the
 * outlet.
 */
function WatchRoutes({ panel }: { panel: string }) {
  return (
    <Routes>
      <Route element={<AuthGate />}>
        <Route path={APP_BASE} element={<WatchLayout />}>
          {groupRoutes()}
          <Route index element={<Navigate to={panel} replace />} />
          <Route path="*" element={<Navigate to={panel} replace />} />
        </Route>
      </Route>
      {/* Everything outside the app shell, the legal pages included. */}
      <Route path="*" element={<Navigate to={panel} replace />} />
    </Routes>
  )
}

export function AppRoutes() {
  const lock = useWatchMode((s) => s.lock)
  const releaseWatch = useWatchMode((s) => s.releaseWatch)
  const { status } = useAuth()

  // **Losing the session drops the lock**, which is the documented way back for
  // somebody who has forgotten the passphrase (entry 6.25).
  //
  // It is also what stops a redirect loop: `AuthGate` answers an expired
  // session with `<Navigate to="/sign-in">`, and the locked tree's catch-all
  // would send that straight back to the panel, for ever.
  useEffect(() => {
    if (lock && (status === 'needs-setup' || status === 'error')) releaseWatch()
  }, [lock, status, releaseWatch])

  if (lock && status === 'ready') return <WatchRoutes panel={lock.panel} />

  return (
    <Routes>
      {/* Pre-auth — outside AdminLayout (§14.11) */}
      <Route path="/sign-in" element={<SignInRoute />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />

      {/* Legal pages — no auth required */}
      <Route path="/legal/privacy" element={<Suspense fallback={null}><PrivacyPage /></Suspense>} />
      <Route path="/legal/terms" element={<Suspense fallback={null}><TermsPage /></Suspense>} />

      {/* Auth gate → app shell */}
      <Route element={<AuthGate />}>
        <Route path={APP_BASE} element={<AdminLayout />}>
          <Route index element={<LandingRedirect />} />
          {groupRoutes()}
          {/* Old link: About is part of Settings now. */}
          <Route path="settings/about" element={<Navigate to={`${APP_BASE}/settings`} replace />} />
          <Route path="*" element={<Navigate to={APP_LANDING_PATH} replace />} />
        </Route>
      </Route>

      {/* Root redirect */}
      <Route path="/" element={<Navigate to={APP_LANDING_PATH} replace />} />
      <Route path="*" element={<Navigate to={APP_LANDING_PATH} replace />} />
    </Routes>
  )
}
